/* eslint-disable no-restricted-syntax */
/* eslint-disable guard-for-in */
import React from "react";
import { chain } from "underscore";
import { Launch16 } from "@carbon/icons-react";
import { DocShape, EmploymentShape, TaxIncomeSection } from "@eave/shared/types/api";
import Money from "web_modules/Money";
import { findFieldValue } from "underwriter_dashboard/shared";

const titleize = require("underscore.string/titleize");

export const sortedEmployments = employments => {
  return chain(employments)
    .sortBy("start_date")
    .sortBy(
      (employment: EmploymentShape) =>
        // make sure current employments are last
        employment.end_date || "9999-12-31",
    )
    .sortBy("borrower_entity")
    .value();
};

export const makeMonthlyIncomeMap = incomeSummary => {
  const incomeMap = {};
  for (const key in incomeSummary) {
    const section = incomeSummary[key];
    if (section && section.hasOwnProperty("calculation_key") && section.calculation_key !== "") {
      incomeMap[section.calculation_key] = section.monthly_average;
    } else {
      for (const i in section) {
        const values = section[i];
        if (values && values.hasOwnProperty("calculation_key") && values.calculation_key !== "") {
          incomeMap[values.calculation_key] = values.monthly_average;
        }
      }
    }
  }
  return incomeMap;
};

export const addIncomeMapToEmployments = (employments): EmploymentShape[] => {
  const updatedEmployments = [...employments];
  for (const i in updatedEmployments) {
    for (const j in updatedEmployments[i].income_aggregations) {
      const { context } = updatedEmployments[i].income_aggregations[j];
      updatedEmployments[i].income_aggregations[j].monthlyIncomeMap = makeMonthlyIncomeMap(context);
    }
  }
  return updatedEmployments;
};

export const compareSubsections = (a, b) => {
  if (
    a.aggregation.sub_key < b.aggregation.sub_key ||
    (a.aggregation.sub_key === b.aggregation.sub_key && a.aggregation.key < b.aggregation.key)
  ) {
    return -1;
  }
  if (a.aggregation.sub_key === b.aggregation.sub_key && a.aggregation.key === b.aggregation.key) {
    return 0;
  }
  return 1;
};

export const getCompositeKey = (aggregation): string => {
  if (aggregation.sub_key === "") {
    return aggregation.key;
  }
  return `${aggregation.key}::${aggregation.sub_key}`;
};

export const renderAggregationValueElement = (fieldAggregation, value) => {
  if (fieldAggregation.type === "money") {
    return <Money value={value} />;
  }
  if (fieldAggregation.type === "number") {
    return value;
  }
  return fieldAggregation.fixed_value;
};

export const renderOperationSymbol = operation => {
  if (operation === "add") {
    return " (+)";
  }
  if (operation === "subtract") {
    return " (-)";
  }
  return "";
};

export const renderOperation = fieldAggregation => {
  if (
    (fieldAggregation.type === "money" || fieldAggregation.type === "number") &&
    !fieldAggregation.excluded_from_total
  ) {
    return renderOperationSymbol(fieldAggregation.operation);
  }
  return "";
};

export const renderFieldTextCell = fieldAggregation => {
  const fieldTextClassName =
    fieldAggregation.operation === "none" || fieldAggregation.excluded_from_total
      ? "tax-return__info"
      : "";
  let referenceText = fieldAggregation.reference_text;
  if (referenceText !== "") {
    referenceText = ` (${referenceText})`;
  }
  const section = fieldAggregation.section.replace("_", " ");
  const sectionText = ` (${titleize(section)})`;
  const operationText = renderOperation(fieldAggregation);
  return (
    <td className={fieldTextClassName}>
      {fieldAggregation.display_text}
      {referenceText}
      {sectionText}
      {operationText}
    </td>
  );
};

export const borrowerEntityToDisplay = borrowerEntityKey => {
  if (!borrowerEntityKey) {
    return "Borrower (not ingested)";
  }
  if (borrowerEntityKey === 3) {
    return "Both borrowers";
  }
  return `Borrower ${borrowerEntityKey}`;
};

export const getAlternateAggregationKeys = aggregation => {
  const soleProprietorshipSet = {
    sole_proprietorship: true,
    sole_proprietorship_ytd_p_and_l: true,
  };
  const reoSet = {
    rental_real_estate: true,
    rental_real_estate_lease: true,
  };

  const alternateAggregationSets = [soleProprietorshipSet, reoSet];
  const matchingAlternateAggregationSet = alternateAggregationSets.find(s => s[aggregation.key]);
  return (
    matchingAlternateAggregationSet &&
    Object.keys(matchingAlternateAggregationSet).filter(agg => agg !== aggregation.key)
  );
};

export const linkToDocument = (doc: DocShape, defaultDocumentName = "Additional Income") => {
  const docDescription = findFieldValue(doc, "document_description");
  return (
    <span key={`${doc.id}-source-document-link`}>
      <a
        href={`/underwriter/app/${doc.loan_application_id}/DocumentsHurdle?d=${doc.id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {docDescription || doc.type || defaultDocumentName} [ID: {doc.id}]{" "}
        <Launch16 style={{ position: "relative", top: "2px" }} />
      </a>
      <br />
    </span>
  );
};

export const reoIDToDisplayAddress = (owned_real_estate, reoID) => {
  const reo = owned_real_estate.find((r: any) => r.id === reoID);
  return reo && reo.address_line1;
};

/**
 * Returns a tax income section, given a section_key
 * @param {TaxIncomeSection[]} sections app.income_summary.tax_summary.sections
 * @param {string} section_key The section_key of the section to return
 */
export const taxIncomeSectionFromSectionKey = (sections: TaxIncomeSection[], section_key: string) =>
  sections.find(section => section.section_key === section_key);
